/**
 * Release Hub — Relative date formatter
 *
 * Uses the native `Intl.RelativeTimeFormat` API (IE11+, all modern browsers)
 * to render dates like "2 napja" (hu), "vor 2 Tagen" (de), "2 днів тому" (uk),
 * "2 days ago" (en) — including proper plural forms — without any hardcoded
 * translation strings.
 *
 * Why this utility exists:
 * - BEFORE (bad): a large switch-on-locale with hardcoded Hungarian, German,
 *   Ukrainian and English literals violated the monorepo's i18n-first rule
 *   (CLAUDE.md: "Minden frontend szöveg `t()` hívással").
 * - AFTER (good): `Intl.RelativeTimeFormat` is backed by ICU/CLDR under the
 *   hood and handles every supported locale's plural rules natively. Adding
 *   a new language requires zero code changes.
 *
 * Edge cases handled:
 * - Empty string → empty string (no crash)
 * - Invalid date string → empty string
 * - Future dates → empty string (release testing is never "in the future")
 *
 * @param dateStr  ISO 8601 timestamp (or `''`/`null`/`undefined` safe)
 * @param locale   App locale from `useTranslation().locale` (hu/en/de/uk)
 * @returns        Localized "X ago" string, or `''` on invalid input
 */
export function formatRelativeDate(dateStr: string | null | undefined, locale: string): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return '';

  const now = Date.now();
  const diffMs = now - date.getTime();

  // Guard: negative delta (future date) → empty string rather than "in 2 days"
  if (diffMs < 0) return '';

  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  // Normalize locale to one of the 4 supported — defaults to 'en' for anything else.
  const supportedLocale = locale === 'hu' || locale === 'de' || locale === 'uk' ? locale : 'en';

  // `numeric: 'auto'` produces natural phrasing: "ma"/"tegnap"/"today"/"yesterday"
  // for the 0-day and 1-day cases, rather than "0 napja"/"1 napja".
  const rtf = new Intl.RelativeTimeFormat(supportedLocale, { numeric: 'auto' });

  if (diffDays >= 1) return rtf.format(-diffDays, 'day');
  if (diffHours >= 1) return rtf.format(-diffHours, 'hour');
  if (diffMinutes >= 1) return rtf.format(-diffMinutes, 'minute');
  return rtf.format(0, 'day'); // "ma" / "today" — under-a-minute delta
}
